
import { WeatherData } from '../types';
import api from './api';

/**
 * Service to fetch real-time aviation weather (METAR) from CheckWX API
 */
export const fetchAirstripWeather = async (code: string, icaoCode?: string): Promise<WeatherData> => {
  // Use ICAO code if available, otherwise use airstrip code
  const weatherCode = (icaoCode || code).toUpperCase().trim();
  
  if (!weatherCode || weatherCode.length < 3) {
    return { temp: 25, condition: 'VFR', wind: 'VAR/03KT', description: 'No METAR available' };
  }

  try {
    // Request real-time METAR from CheckWX API
    const response = await api.get(`/weather/${weatherCode}?realtime=true`);
    const data = response.data?.data;
    
    if (!data) {
      return { temp: 25, condition: 'VFR', wind: 'VAR/03KT', description: 'No METAR available' };
    }

    // Parse CheckWX decoded response
    const visibility = Number(data.visibility ?? 999);
    let condition: WeatherData['condition'] = 'VFR';
    
    // Use flight_category from CheckWX if available, otherwise determine from visibility
    if (data.conditions) {
      const fc = String(data.conditions).toUpperCase();
      if (fc.includes('IFR')) condition = 'IFR';
      else if (fc.includes('MVFR') || fc.includes('LIFR')) condition = 'MVFR';
      else condition = 'VFR';
    } else {
      if (visibility <= 3) {
        condition = 'IFR';
      } else if (visibility <= 5) {
        condition = 'MVFR';
      }
    }

    const windDirection = data.windDirection !== null && data.windDirection !== undefined 
      ? String(Math.round(data.windDirection)).padStart(3, '0') 
      : 'VAR';
    const windSpeed = data.windSpeed !== null && data.windSpeed !== undefined 
      ? `${Math.round(data.windSpeed)}KT` 
      : 'Calm';

    return {
      temp: Number(data.temperature || 0),
      condition,
      wind: `${windDirection}/${windSpeed}`,
      description: data.conditions || data.rawMETAR || data.metar || 'Conditions Stable',
    };
  } catch (error) {
    console.error(`Failed to fetch METAR for ${weatherCode}:`, error);
    return { temp: 25, condition: 'VFR', wind: 'VAR/03KT', description: 'No METAR available' };
  }
};
